import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Grid2, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { IconTrash, IconEdit, IconEye, IconDotsVertical, IconPlus, IconSearch, IconRestore, IconFileArrowRight } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import StatusChip from "@components/chip/statusChip";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { formatDate } from "@utils/formatter";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { applyFilters, createSearchFilter, createCustomFilter, createDateRangeFilter } from "@utils/filter";


const defaultQueryFields = {
  search: "",
  postName: "",
  paymentMethod: null,
  paymentStatus: null,
  dateFrom: "",
  dateTo: "",
};

export default function ActivityPayment() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const { isCurrentModuleAdmin } = useAuthentication()

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      search: "",
      postName: "",
      paymentMethod: null,
      paymentStatus: null,
      dateFrom: "",
      dateTo: "",
    }
  })

  const [searchQuery, setSearchQuery] = useState(defaultQueryFields)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null)

  const { isFetching, error, data } = useQuery({
    queryKey: ['getActivityPayment'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getActivityPayment),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
    },
  })

  const paymentStatusOptions = useQuery({
    queryKey: ['getActivityPaymentStatusList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getActivityPaymentStatusList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      // console.log(data)
      return data.map(item => ({
        statusGuid: item.parameterGuid,
        status: item.valueEng
      }));
    },
  })

  const paymentMethodOptions = useQuery({
    queryKey: ['getActivityPaymentMethod'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getActivityPaymentMethod),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        value: item.parameterGuid,
        label: item.valueEng
      }));
    },
  })

  //================================Table================================

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedRow(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          component={RouterLink}
          to={`view?id=${selectedRow?.activityPaymentGuid}`}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================
          
  const handleClickSearch = (data) => {
    setSearchQuery({...data,
      paymentStatus: data?.paymentStatus?.statusGuid,
      paymentMethod: data?.paymentMethod?.value,
    })
  }

  const handleClickReset = () => {
    reset(defaultQueryFields)
    setSearchQuery(defaultQueryFields)
  }

  const filters = [
    createSearchFilter(searchQuery.search, ["paymentNo"]),
    createCustomFilter(searchQuery.postName, 'applications[0].post.postName', true),
    createCustomFilter(searchQuery.paymentStatus, 'paymentStatus.parameterGuid', true),
    createCustomFilter(searchQuery.paymentMethod, 'paymentMethod.parameterGuid', true),
    createDateRangeFilter(searchQuery.dateFrom, searchQuery.dateTo, 'paymentDate')
  ];

  const filteredData = applyFilters(data, filters);

  const postNameOptions = [
    ...new Set(
      data.map((item) => item?.applications?.[0]?.post?.postName).filter(Boolean)
    ),
  ];

  //======================================================================

  const saveAs = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportToExcel = () => {
    const mappedData = filteredData.map(item => ({
      "Payment No": item?.paymentNo,
      "Post Name": item?.applications[0]?.post?.postName,
      "Paid On": formatDate(item?.paymentDate),
      "Amount (RM)": item?.applications[0]?.postFeeOption?.fee,
      "Method": item?.paymentMethod?.valueEng,
      "Payment Status": item?.paymentStatus?.valueEng
    }))
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(mappedData);

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Generate a binary string for the workbook
    const workbookBinary = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "binary",
    });

    // Convert the binary string to an array buffer
    const buffer = new ArrayBuffer(workbookBinary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < workbookBinary.length; i++) {
      view[i] = workbookBinary.charCodeAt(i) & 0xff;
    }

    // Create a blob from the buffer and trigger a download
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, `ActivityPayment-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          isCurrentModuleAdmin &&
          <CustomButton component={RouterLink} to={"create"} startIcon={<IconPlus size={13} />}>Create</CustomButton>
        }
      />
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={{ xs: 12 }}>
            <Typography
              fontSize={"0.9rem"}
              color={"#202020"}
              fontWeight={600}
            >
              Search
            </Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="search"
              control={control}
              label="Search"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="postName"
              control={control}
              label="Post Name"
              loading={isFetching}
              options={postNameOptions}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="paymentMethod"
              control={control}
              label="Method"
              loading={paymentMethodOptions.isFetching}
              options={paymentMethodOptions.data}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="paymentStatus"
              control={control}
              label="Status"
              loading={paymentStatusOptions.isFetching}
              options={paymentStatusOptions.data}
              getOptionLabel={(option) => option.status}
              getOptionKey={(option) => option.statusGuid}
              isOptionEqualToValue={(option, value) => option.statusGuid === value?.statusGuid}
            />
          </Grid2>

          <Grid2 size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <ControlledDatePicker 
              name="dateFrom"
              control={control}
              label="Date From"
            />
          </Grid2>

          <Grid2 size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <ControlledDatePicker 
              name="dateTo"
              control={control}
              label="Date To"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
            <CustomIconButton aria-label="Reset" color="#999999" onClick={handleClickReset}>
              <IconRestore />
            </CustomIconButton>
            <CustomIconButton aria-label="Search" onClick={handleSubmit(handleClickSearch)}>
              <IconSearch />
            </CustomIconButton>
            <CustomIconButton aria-label="Export to Excel" onClick={exportToExcel} disabled={filteredData.length === 0} color="#007BFF">
              <IconFileArrowRight />
            </CustomIconButton>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper
        sx={{
          p: "0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <DataGrid
          loading={isFetching}
          rows={filteredData}
          localeText={{ noRowsLabel: 'No records found' }}
          getRowId={(row) => row.activityPaymentGuid}
          columns={[
            {
              field: 'paymentNo',
              headerName: 'Payment No',
              flex: 1, minWidth: 125,
            },
            {
              field: 'postName',
              headerName: 'Post Name',
              flex: 1, minWidth: 125,
              valueGetter: (value, row) => row?.applications[0]?.post?.postName,
            },
            {
              field: 'paymentDate',
              headerName: 'Paid On',
              flex: 1, minWidth: 125,
              valueGetter: (value) => formatDate(value),
            },
            {
              field: 'amount',
              headerName: 'Amount (RM)',
              flex: 1, minWidth: 180,
              valueGetter: (value, row) => row?.applications[0]?.postFeeOption?.fee,
            },
            {
              field: 'paymentMethod',
              headerName: 'Method',
              flex: 1, minWidth: 180,
              valueGetter: (value) => value?.valueEng,
            },
            {
              field: 'paymentStatus',
              headerName: 'Payment Status',
              flex: 1, minWidth: 120,
              renderCell: (params) => (
                <StatusChip  status={params.row.paymentStatus?.valueEng} />
              ),
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton aria-label="Actions" onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
    </div>
  );
}